import DOMPurify from 'dompurify';
import { isHtmlContent } from '@/lib/utils';

/** Preserve legacy plain text when it first enters Tiptap's HTML API. */
export function richTextEditorContent(value?: string | null): string {
  if (!value || isHtmlContent(value)) return value ?? '';
  const escaped = value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
  return `<p>${escaped.replace(/\r?\n/g, '<br>')}</p>`;
}

/**
 * Explicit rich-text styling for editor + read surfaces.
 *
 * `@tailwindcss/typography` is not installed, so `prose` classes are no-ops and
 * Tailwind's preflight resets lists/headings to plain text. These arbitrary
 * variants restyle the rendered HTML (lists, headings, links, paragraphs) and
 * win over the reset by specificity — without the app-wide blast radius of
 * enabling the typography plugin. Apply to the editor content and every place
 * that renders the stored HTML (estimate detail, public quote).
 *
 * Cover every block the editor schema admits, not just the ones its toolbar has
 * a button for: `RichTextPdf` maps h1–h6 and blockquote, and Tiptap's markdown
 * input rules (`### `, `> `) and paste handling produce them without a button.
 * A block styled in the PDF but not here renders as ordinary body copy on the
 * customer's quote and bold in their PDF copy of the same document.
 */
export const RICH_TEXT_CLASS =
  'font-sans [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 ' +
  '[&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mt-2 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-2 ' +
  '[&_h3]:font-semibold [&_h3]:mt-2 [&_h4]:font-semibold [&_h4]:mt-2 ' +
  '[&_h5]:font-semibold [&_h5]:mt-2 [&_h6]:font-semibold [&_h6]:mt-2 ' +
  '[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 ' +
  '[&_a]:text-blue-600 [&_a]:underline [&_p]:my-1';

/**
 * Subdued variant for compact previews (e.g. a line-clamped card summary).
 * Keeps the list/paragraph reset so bullets render, but holds headings and
 * links to the surrounding muted/small chrome instead of full typography sizing
 * — so the rendered HTML stays a quiet summary rather than dominating the card.
 */
export const RICH_TEXT_PREVIEW_CLASS =
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 ' +
  '[&_h1]:text-xs [&_h1]:font-medium [&_h1]:mt-0 [&_h2]:text-xs [&_h2]:font-medium [&_h2]:mt-0 ' +
  '[&_h3]:text-xs [&_h3]:font-medium [&_h3]:mt-0 [&_h4]:text-xs [&_h4]:font-medium [&_h4]:mt-0 ' +
  '[&_h5]:text-xs [&_h5]:font-medium [&_h5]:mt-0 [&_h6]:text-xs [&_h6]:font-medium [&_h6]:mt-0 ' +
  '[&_blockquote]:my-1 [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-2 ' +
  '[&_a]:underline [&_p]:my-0.5';

/**
 * Does this stored HTML actually say anything?
 *
 * Notes are authored in the rich-text editor, so `content.trim()` is truthy for
 * the editor's own empty payloads — `<p></p>`, `<p><br></p>` — which render a
 * blank block under a heading. Test the rendered TEXT instead, sanitizing first
 * so the string this asks about is the string that would render.
 *
 * Shared deliberately: the admin story card and the public share page both
 * gate on this, and two copies would drift the moment one is fixed.
 */
export function hasRenderableText(content?: string | null): boolean {
  if (!content) return false;
  const parsed = new DOMParser().parseFromString(DOMPurify.sanitize(content), 'text/html');
  return (parsed.body.textContent ?? '').trim().length > 0;
}
